import React, { useState, useEffect, useRef, useCallback } from 'react';
import TaskCard from './TaskCard';
import { KANBAN_COLUMNS } from '../../constants/taskStatuses';

const COLUMN_STYLES = {
  todo: { headerBg: 'bg-gray-50 dark:bg-gray-700/30', headerBorder: 'border-gray-200 dark:border-gray-600', icon: '📋' },
  in_progress: { headerBg: 'bg-blue-50 dark:bg-blue-900/20', headerBorder: 'border-blue-200 dark:border-blue-700', icon: '🔄' },
  blocked: { headerBg: 'bg-red-50 dark:bg-red-900/20', headerBorder: 'border-red-200 dark:border-red-700', icon: '🚫' },
  done: { headerBg: 'bg-green-50 dark:bg-green-900/20', headerBorder: 'border-green-200 dark:border-green-700', icon: '✅' },
};

// Default mobile collapse state — focus on actionable columns
const DEFAULT_OPEN = { todo: true, in_progress: true, blocked: false, done: false };

// Pointer travel (px) before a hold becomes a drag — below this a pointer-up
// is treated as a plain click (opens the task). Mirrors the Gantt's
// no-move-is-a-click discipline so click-to-open never breaks.
const DRAG_THRESHOLD = 5;

/**
 * Kanban board with optional drag-and-drop between status columns.
 *
 * Props:
 *  - tasks, onTaskClick — as before (click a card to open it).
 *  - canDrag: boolean OR (task) => boolean. When true (and onStatusChange is
 *    given) cards can be dragged to another column. A function lets multi-team
 *    boards gate per-card (e.g. lead-of-this-task's-team). Default false →
 *    click-only, exactly the old behaviour.
 *  - onStatusChange(task, newStatus): performs the change. MUST resolve on
 *    success and REJECT/throw on failure (the board reverts the optimistic
 *    move and shows an error on rejection).
 *
 * Drag uses pointer events + a custom ghost (no dependency), matching the
 * Gantt's window-drag approach; touch is covered by pointer events.
 */
export default function TaskKanbanView({ tasks, onTaskClick, canDrag = false, onStatusChange }) {
  const [openCols, setOpenCols] = useState(() => {
    try {
      const raw = localStorage.getItem('sfx_kanban_sections');
      return raw ? { ...DEFAULT_OPEN, ...JSON.parse(raw) } : DEFAULT_OPEN;
    } catch {
      return DEFAULT_OPEN;
    }
  });

  useEffect(() => {
    try { localStorage.setItem('sfx_kanban_sections', JSON.stringify(openCols)); } catch { /* ignore */ }
  }, [openCols]);

  const toggleCol = (key) => setOpenCols(p => ({ ...p, [key]: !p[key] }));

  const dndEnabled = typeof onStatusChange === 'function';
  const canDragTask = useCallback(
    (task) => dndEnabled && (typeof canDrag === 'function' ? !!canDrag(task) : !!canDrag),
    [canDrag, dndEnabled]
  );

  // ── Drag state ──────────────────────────────────────────────
  // dragRef holds the live, mutable gesture data (avoids stale closures in
  // the window listeners); dragTick forces re-renders for the ghost/highlight.
  const dragRef = useRef(null);
  const [, setDragTick] = useState(0);
  const [active, setActive] = useState(false); // window listeners on while true
  const justDraggedRef = useRef(0);            // suppress the click after a real drag
  const columnRefs = useRef({});               // status -> column body element

  // Optimistic move: the dragged task shows in the target column immediately.
  // Cleared when props catch up (success) or on failure (revert).
  const [pendingMove, setPendingMove] = useState(null); // { taskId, from, to }
  const [justMovedId, setJustMovedId] = useState(null); // brief settle highlight
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!pendingMove) return;
    const t = tasks.find((x) => x.id === pendingMove.taskId);
    // Props reflect the move (or the task vanished) → drop the override.
    if (!t || t.status === pendingMove.to) setPendingMove(null);
  }, [tasks, pendingMove]);

  const effectiveStatus = (task) =>
    (pendingMove && pendingMove.taskId === task.id ? pendingMove.to : task.status);

  const hitTestColumn = (x, y) => {
    for (const col of KANBAN_COLUMNS) {
      const el = columnRefs.current[col.key];
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) return col.key;
    }
    return null;
  };

  // Latest-handler refs so the window listeners (attached once per gesture)
  // always call current logic without re-subscribing.
  const moveRef = useRef();
  const upRef = useRef();

  moveRef.current = (e) => {
    const d = dragRef.current;
    if (!d) return;
    if (!d.dragging) {
      if (Math.hypot(e.clientX - d.startX, e.clientY - d.startY) < DRAG_THRESHOLD) return;
      d.dragging = true;
      d.offsetX = d.startX - d.rect.left;
      d.offsetY = d.startY - d.rect.top;
    }
    d.x = e.clientX;
    d.y = e.clientY;
    d.over = hitTestColumn(e.clientX, e.clientY);
    e.preventDefault();
    setDragTick((t) => t + 1);
  };

  upRef.current = async () => {
    const d = dragRef.current;
    dragRef.current = null;
    setActive(false);
    setDragTick((t) => t + 1);
    if (!d) return;
    if (!d.dragging) return; // no travel → it was a click; onClick will fire
    justDraggedRef.current = Date.now();
    const target = d.over;
    if (!target || target === d.from) return; // dropped outside / same column → snap back

    setPendingMove({ taskId: d.task.id, from: d.from, to: target });
    setError(null);
    try {
      await onStatusChange(d.task, target);
      setJustMovedId(d.task.id);
      setTimeout(() => setJustMovedId(null), 700);
      // pendingMove clears itself once props reflect the new status.
    } catch {
      setPendingMove(null); // revert to the origin column
      setError('Could not move the task — please try again.');
    }
  };

  useEffect(() => {
    if (!active) return undefined;
    const m = (e) => moveRef.current(e);
    const u = (e) => upRef.current(e);
    window.addEventListener('pointermove', m, { passive: false });
    window.addEventListener('pointerup', u);
    window.addEventListener('pointercancel', u);
    return () => {
      window.removeEventListener('pointermove', m);
      window.removeEventListener('pointerup', u);
      window.removeEventListener('pointercancel', u);
    };
  }, [active]);

  const startDrag = (e, task) => {
    if (e.button != null && e.button !== 0) return; // left / touch only
    const rect = e.currentTarget.getBoundingClientRect();
    dragRef.current = {
      task, from: task.status, dragging: false,
      startX: e.clientX, startY: e.clientY, rect,
      x: e.clientX, y: e.clientY, offsetX: 0, offsetY: 0, width: rect.width, over: task.status,
    };
    setActive(true);
  };

  const columns = KANBAN_COLUMNS.map((col) => ({
    ...col,
    tasks: tasks.filter((t) => effectiveStatus(t) === col.key).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    style: COLUMN_STYLES[col.key],
  }));

  const d = dragRef.current;
  const isDraggingNow = !!(d && d.dragging);
  const overCol = isDraggingNow ? d.over : null;
  const draggingId = isDraggingNow ? d.task.id : null;

  return (
    <div>
      {error && (
        <div className="mb-3 px-3 py-2 text-sm rounded-lg bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800">
          {error}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {columns.map((col) => {
          const isOpen = openCols[col.key];
          const isOver = overCol === col.key;
          const isDropTarget = isOver && d && d.from !== col.key;
          return (
            <div key={col.key} className="flex flex-col md:min-h-[200px]">
              {/* Column header — tappable toggle on mobile, static on desktop */}
              <button
                type="button"
                onClick={() => toggleCol(col.key)}
                className={`flex md:cursor-default items-center gap-2 px-3 py-2 md:py-2 min-h-[44px] md:min-h-0 rounded-t-lg md:rounded-t-lg border ${!isOpen ? 'rounded-b-lg md:rounded-b-none' : ''} ${col.style.headerBg} ${col.style.headerBorder} w-full text-left transition-colors`}
                aria-expanded={isOpen}
              >
                {/* Mobile-only chevron */}
                <svg
                  className={`md:hidden h-4 w-4 text-gray-500 dark:text-gray-400 transition-transform ${isOpen ? '' : '-rotate-90'}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
                <span>{col.style.icon}</span>
                <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{col.label}</span>
                <span className="ml-auto text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600">
                  {col.tasks.length}
                </span>
              </button>

              {/* Column body — highlights as a drop target while dragging */}
              <div
                ref={(el) => { columnRefs.current[col.key] = el; }}
                className={`${isOpen ? 'block' : 'hidden'} md:block flex-1 p-2 space-y-2 border border-t-0 rounded-b-lg md:overflow-y-auto md:max-h-[60vh] transition-colors ${
                  isDropTarget
                    ? 'border-blue-400 dark:border-blue-500 ring-2 ring-blue-400/60 dark:ring-blue-500/50 bg-blue-50/60 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50'
                }`}
              >
                {col.tasks.length === 0 && !isDropTarget ? (
                  <p className="text-xs text-gray-500 dark:text-gray-400 italic text-center py-4">No tasks</p>
                ) : (
                  col.tasks.map((task) => {
                    const draggable = canDragTask(task);
                    const isPlaceholder = draggingId === task.id;
                    // Pete: the pickup lift+tilt fires on CLICK (pointer-down),
                    // not once you start moving — so the card pops the moment
                    // you press it, then the drag just carries the lifted card.
                    const isPressed = !!d && !d.dragging && d.task.id === task.id;
                    return (
                      <div
                        key={task.id}
                        onPointerDown={draggable ? (e) => startDrag(e, task) : undefined}
                        style={{
                          touchAction: draggable ? 'none' : undefined,
                          transition: 'transform 120ms ease-out',
                          ...(isPressed ? { transform: 'scale(1.03) rotate(3.5deg)', position: 'relative', zIndex: 40 } : null),
                        }}
                        className={`${draggable ? 'cursor-grab active:cursor-grabbing' : ''} ${
                          isPressed ? 'shadow-2xl rounded-lg ring-1 ring-black/5 dark:ring-white/10' : ''
                        } ${
                          isPlaceholder ? 'opacity-40 border-2 border-dashed border-gray-400 dark:border-gray-500 rounded-lg' : ''
                        } ${justMovedId === task.id ? 'rounded-lg ring-2 ring-green-400 dark:ring-green-500' : ''}`}
                      >
                        <TaskCard
                          task={task}
                          onClick={() => { if (Date.now() - justDraggedRef.current < 300) return; onTaskClick(task); }}
                        />
                      </div>
                    );
                  })
                )}
                {/* Insertion indicator at the drop target */}
                {isDropTarget && (
                  <div className="h-1.5 rounded-full bg-blue-400 dark:bg-blue-500" />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Drag ghost — the lifted card following the pointer */}
      {isDraggingNow && (
        <div
          className="fixed z-50 pointer-events-none"
          style={{
            left: d.x - d.offsetX,
            top: d.y - d.offsetY,
            width: d.width,
            transform: 'scale(1.03) rotate(3.5deg)',
            transition: 'transform 120ms ease-out',
          }}
        >
          <div className="shadow-2xl rounded-lg ring-1 ring-black/5 dark:ring-white/10">
            <TaskCard task={d.task} />
          </div>
        </div>
      )}
    </div>
  );
}
